import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  AlertTriangle,
  Users,
  Clock,
  Activity,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConsultantRow {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  is_2fa_enabled: boolean | null;
  status: string | null;
}

interface ClientRow {
  user_id: string;
  company_name: string | null;
  full_name: string | null;
  email: string | null;
  is_2fa_enabled: boolean | null;
}

interface AdminRow {
  user_id: string;
  role: string | null;
  is_2fa_enabled: boolean | null;
}

interface LoginRecord {
  id: string;
  user_id: string | null;
  portal: string | null;
  event_type: string | null;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

interface Stats {
  totalWith2FA: number;
  consultantsWith2FA: number;
  clientsWith2FA: number;
  adminsWith2FA: number;
}

type TabKey = 'consultants' | 'clients' | 'admins' | 'activity';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function truncateId(id: string | null): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function formatDate(ts: string): string {
  return new Date(ts).toLocaleString();
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TwoFactorAudit() {
  const [activeTab, setActiveTab] = useState<TabKey>('consultants');

  // Data
  const [consultants, setConsultants] = useState<ConsultantRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loginRecords, setLoginRecords] = useState<LoginRecord[]>([]);
  const [stats, setStats] = useState<Stats>({ totalWith2FA: 0, consultantsWith2FA: 0, clientsWith2FA: 0, adminsWith2FA: 0 });

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consultantSearch, setConsultantSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [activityPortalFilter, setActivityPortalFilter] = useState('all');

  // ── Loaders ─────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, clRes, aRes] = await Promise.all([
        supabase
          .from('consultant_profiles')
          .select('user_id, first_name, last_name, email, is_2fa_enabled, status'),
        supabase
          .from('workday_client_profiles')
          .select('user_id, company_name, full_name, email, is_2fa_enabled'),
        supabase
          .from('user_roles')
          .select('user_id, role, is_2fa_enabled')
          .eq('role', 'admin'),
      ]);

      if (cRes.error) throw cRes.error;
      if (clRes.error) throw clRes.error;
      if (aRes.error) throw aRes.error;

      const consultantData = (cRes.data ?? []) as ConsultantRow[];
      const clientData = (clRes.data ?? []) as ClientRow[];
      const adminData = (aRes.data ?? []) as AdminRow[];

      setConsultants(consultantData);
      setClients(clientData);
      setAdmins(adminData);

      const consultantsWith2FA = consultantData.filter(r => r.is_2fa_enabled).length;
      const clientsWith2FA = clientData.filter(r => r.is_2fa_enabled).length;
      const adminsWith2FA = adminData.filter(r => r.is_2fa_enabled).length;

      setStats({
        totalWith2FA: consultantsWith2FA + clientsWith2FA + adminsWith2FA,
        consultantsWith2FA,
        clientsWith2FA,
        adminsWith2FA,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLoginRecords = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('login_records')
      .select('id, user_id, portal, event_type, created_at, ip_address, user_agent')
      .order('created_at', { ascending: false })
      .limit(50);
    if (err) {
      setError(err.message);
    } else {
      setLoginRecords((data ?? []) as LoginRecord[]);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (activeTab === 'activity') {
      loadLoginRecords();
    }
  }, [activeTab, loadLoginRecords]);

  // ── Derived / filtered data ──────────────────────────────────────────────────

  const filteredConsultants = consultants.filter(c => {
    const q = consultantSearch.toLowerCase();
    const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
    return name.includes(q) || (c.email ?? '').toLowerCase().includes(q);
  });

  const filteredClients = clients.filter(c => {
    const q = clientSearch.toLowerCase();
    const label = (c.company_name ?? c.full_name ?? c.email ?? '').toLowerCase();
    return label.includes(q) || (c.email ?? '').toLowerCase().includes(q);
  });

  const filteredActivity = activityPortalFilter === 'all'
    ? loginRecords
    : loginRecords.filter(r => r.portal === activityPortalFilter);

  const uniquePortals = Array.from(new Set(loginRecords.map(r => r.portal).filter(Boolean)));

  // ── Render helpers ───────────────────────────────────────────────────────────

  const TwoFABadge = ({ enabled }: { enabled: boolean | null }) =>
    enabled ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
        <CheckCircle className="w-3.5 h-3.5" />
        Enabled
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-medium">
        <XCircle className="w-3.5 h-3.5" />
        Disabled
      </span>
    );

  const EmptyState = ({ message }: { message: string }) => (
    <tr>
      <td colSpan={99} className="px-6 py-12 text-center text-gray-500 text-sm">
        {message}
      </td>
    </tr>
  );

  const tableHeaderClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
  const tableCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-gray-700';

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-600" />
              2FA Security Audit
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Review two-factor authentication adoption across all portals
            </p>
          </div>
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Stats row */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-5 animate-pulse h-24" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Shield className="w-5 h-5 text-green-600" />}
              bg="bg-green-50"
              label="Total Users with 2FA"
              value={stats.totalWith2FA}
            />
            <StatCard
              icon={<Users className="w-5 h-5 text-blue-600" />}
              bg="bg-blue-50"
              label="Consultants with 2FA"
              value={stats.consultantsWith2FA}
            />
            <StatCard
              icon={<ShieldCheck className="w-5 h-5 text-teal-600" />}
              bg="bg-teal-50"
              label="Clients with 2FA"
              value={stats.clientsWith2FA}
            />
            <StatCard
              icon={<ShieldOff className="w-5 h-5 text-orange-600" />}
              bg="bg-orange-50"
              label="Admins with 2FA"
              value={stats.adminsWith2FA}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
              {(['consultants', 'clients', 'admins', 'activity'] as TabKey[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab
                      ? 'border-green-600 text-green-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'consultants' && <Users className="w-4 h-4" />}
                  {tab === 'clients' && <ShieldCheck className="w-4 h-4" />}
                  {tab === 'admins' && <Shield className="w-4 h-4" />}
                  {tab === 'activity' && <Activity className="w-4 h-4" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1).replace('activity', 'Login Activity')}
                </button>
              ))}
            </nav>
          </div>

          {/* ── Consultants ── */}
          {activeTab === 'consultants' && (
            <div>
              <div className="p-4 border-b border-gray-100">
                <div className="relative max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name or email…"
                    value={consultantSearch}
                    onChange={e => setConsultantSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={tableHeaderClass}>Name</th>
                      <th className={tableHeaderClass}>Email</th>
                      <th className={tableHeaderClass}>2FA Status</th>
                      <th className={tableHeaderClass}>Account Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-8 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                          </div>
                        </td>
                      </tr>
                    ) : filteredConsultants.length === 0 ? (
                      <EmptyState message="No consultants found." />
                    ) : (
                      filteredConsultants.map(c => (
                        <tr key={c.user_id} className="hover:bg-gray-50">
                          <td className={tableCellClass}>
                            {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td className={tableCellClass}>{c.email ?? '—'}</td>
                          <td className={tableCellClass}>
                            <TwoFABadge enabled={c.is_2fa_enabled} />
                          </td>
                          <td className={tableCellClass}>
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              c.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : c.status === 'inactive'
                                ? 'bg-gray-100 text-gray-600'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {c.status ?? 'unknown'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Clients ── */}
          {activeTab === 'clients' && (
            <div>
              <div className="p-4 border-b border-gray-100">
                <div className="relative max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by company or email…"
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={tableHeaderClass}>Company / Name</th>
                      <th className={tableHeaderClass}>Email</th>
                      <th className={tableHeaderClass}>2FA Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-8 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                          </div>
                        </td>
                      </tr>
                    ) : filteredClients.length === 0 ? (
                      <EmptyState message="No clients found." />
                    ) : (
                      filteredClients.map(c => (
                        <tr key={c.user_id} className="hover:bg-gray-50">
                          <td className={tableCellClass}>
                            {c.company_name ?? c.full_name ?? '—'}
                          </td>
                          <td className={tableCellClass}>{c.email ?? '—'}</td>
                          <td className={tableCellClass}>
                            <TwoFABadge enabled={c.is_2fa_enabled} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Admins ── */}
          {activeTab === 'admins' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={tableHeaderClass}>User ID</th>
                    <th className={tableHeaderClass}>Role</th>
                    <th className={tableHeaderClass}>2FA Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                        </div>
                      </td>
                    </tr>
                  ) : admins.length === 0 ? (
                    <EmptyState message="No admin records found." />
                  ) : (
                    admins.map(a => (
                      <tr key={a.user_id} className="hover:bg-gray-50">
                        <td className={`${tableCellClass} font-mono text-xs text-gray-500`}>
                          {truncateId(a.user_id)}
                        </td>
                        <td className={tableCellClass}>
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                            {a.role ?? '—'}
                          </span>
                        </td>
                        <td className={tableCellClass}>
                          <TwoFABadge enabled={a.is_2fa_enabled} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Login Activity ── */}
          {activeTab === 'activity' && (
            <div>
              <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Recent 50 login records</span>
                <select
                  value={activityPortalFilter}
                  onChange={e => setActivityPortalFilter(e.target.value)}
                  className="ml-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="all">All Portals</option>
                  {uniquePortals.map(p => (
                    <option key={p!} value={p!}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className={tableHeaderClass}>User ID</th>
                      <th className={tableHeaderClass}>Portal</th>
                      <th className={tableHeaderClass}>Event Type</th>
                      <th className={tableHeaderClass}>IP Address</th>
                      <th className={tableHeaderClass}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredActivity.length === 0 ? (
                      <EmptyState message="No login records found." />
                    ) : (
                      filteredActivity.map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className={`${tableCellClass} font-mono text-xs text-gray-500`}>
                            {truncateId(r.user_id)}
                          </td>
                          <td className={tableCellClass}>
                            {r.portal ? (
                              <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                                {r.portal}
                              </span>
                            ) : '—'}
                          </td>
                          <td className={tableCellClass}>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              r.event_type === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : r.event_type === 'success'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {r.event_type ?? '—'}
                            </span>
                          </td>
                          <td className={`${tableCellClass} font-mono text-xs`}>
                            {r.ip_address ?? '—'}
                          </td>
                          <td className={tableCellClass}>
                            {formatDate(r.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  icon,
  bg,
  label,
  value,
}: {
  icon: React.ReactNode;
  bg: string;
  label: string;
  value: number;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}
